import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ArrowUp01Icon, ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ReactNode, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";

type Column<T> = {
  header: ReactNode;
  accessor: (row: T, index: number) => ReactNode;
  widthClass?: string;
};

type ExpandableTableProps<T> = {
  id?: string;
  columns: Column<T>[];
  rows: T[];
  renderExpanded: (row: T) => ReactNode;
  cellClassName?: string;
  rowClassName?: string;
  headerRowClassName?: string;
  hideHeader?: boolean;
  getViewUrl?: (row: T) => string | undefined;
  hideExpandAll?: boolean;
};

export function ExpandableTable<T extends object>(
  props: ExpandableTableProps<T>
) {
  const { id, columns, rows, renderExpanded, hideHeader, getViewUrl } = props;
  const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(
    new Set()
  );

  const allExpanded = rows.length > 0 && expandedIndexes.size === rows.length;

  const toggleIndex = (index: number) => {
    setExpandedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    setExpandedIndexes(
      allExpanded ? new Set() : new Set(rows.map((_, i) => i))
    );
  };

  return (
    <div className="space-y-1" id={id || ""}>
      <Hide if={Boolean(rows.length === 0 || props.hideExpandAll)}>
        <div className="mb-4 flex justify-end">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            HugeIcon={allExpanded ? ArrowUp01Icon : ArrowDown01Icon}
            iconPosition="left"
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        </div>
      </Hide>

      <Hide if={hideHeader}>
        <div
          className={cx(
            "flex border-b border-alpha-100",
            props.headerRowClassName
          )}
        >
          {columns.map((col, i) => (
            <p
              key={i}
              className={cx(props.cellClassName, col.widthClass ?? "flex-1")}
            >
              {col.header}
            </p>
          ))}
          <Show if={Boolean(getViewUrl)}>
            <div className={cx(props.cellClassName, "w-12 shrink-0")} />
          </Show>
          <div className={cx(props.cellClassName, "w-12 shrink-0")} />
        </div>
      </Hide>

      {rows.map((row, i) => (
        <div key={i}>
          <div
            className={cx(
              "flex cursor-pointer hover:bg-alpha-20 h-[72px]",
              props.rowClassName
            )}
            onClick={() => toggleIndex(i)}
          >
            {columns.map((col, j) => (
              <p
                key={j}
                className={cx(props.cellClassName, col.widthClass ?? "flex-1")}
              >
                {col.accessor(row, i)}
              </p>
            ))}
            <Show if={Boolean(getViewUrl)}>
              <div
                key={`view_${i}`}
                className={cx(props.cellClassName, "w-12")}
                onClick={(event) => event.stopPropagation()}
              >
                <Show if={Boolean(getViewUrl?.(row))}>
                  <Tooltip>
                    <TooltipTrigger>
                      <IconButton
                        HugeIcon={ViewIcon}
                        size={IconButtonSize.SM}
                        className="bg-white"
                        aria-label="View file"
                        onClick={() =>
                          window.open(
                            getViewUrl?.(row),
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      />
                    </TooltipTrigger>
                    <TooltipContent className="z-[50] max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                      View file
                    </TooltipContent>
                  </Tooltip>
                </Show>
              </div>
            </Show>

            <div
              key={`expander_${i}`}
              className={cx(props.cellClassName, "w-12")}
            >
              <IconButton
                HugeIcon={
                  expandedIndexes.has(i) ? ArrowUp01Icon : ArrowDown01Icon
                }
                size={IconButtonSize.SM}
                className="bg-white"
              />
            </div>
          </div>

          <Show if={expandedIndexes.has(i)}>{renderExpanded(row)}</Show>
        </div>
      ))}
    </div>
  );
}
